import { useEffect, useState } from "react"

export type DeviceMode =
  | "portrait"
  | "landscape"
  | "standalone-portrait"
  | "standalone-landscape"

type OrientationOptions = {
  portraitBreakpoint?: number
}

type OrientationReturn = {
  isPortrait: boolean
  isLandscape: boolean
  isStandalone: boolean

  mode: DeviceMode

  isMobileWeb: boolean
  isStandalonePortrait: boolean
  isStandaloneLandscape: boolean

  isPhone: boolean
  isPhoneLandscape: boolean

  // NUEVO
  isMobileStandalone: boolean
  isDesktopStandalone: boolean

  isPortraitLarge: boolean
  isPortraitSmall: boolean
}

export default function useOrientation(
  options: OrientationOptions = {}
): OrientationReturn {
  const {
    portraitBreakpoint = 500,
  } = options

  const getState = () => {
    if (typeof window === "undefined") {
      return {
        isPortrait: true,
        isLandscape: false,
        isStandalone: false,

        isPhone: false,
        isPhoneLandscape: false,

        isMobileStandalone: false,
        isDesktopStandalone: false,

        isPortraitLarge: false,
        isPortraitSmall: true,
      }
    }

    // Orientación real
    const isPortrait = window.innerHeight >= window.innerWidth
    const isLandscape = !isPortrait

    // Standalone (iOS + Android + Desktop PWA)
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any)?.standalone === true

    // Tipo de dispositivo
const isDesktop =
  window.matchMedia("(pointer: fine)").matches &&
  window.innerWidth >= 1024

const isPhone = !isDesktop

// Standalone móvil vs escritorio
const isDesktopStandalone =
  isStandalone && isDesktop

const isMobileStandalone =
  isStandalone && !isDesktop

    // Portrait pequeño / grande
    const isPortraitLarge =
      isPortrait &&
      window.innerWidth > portraitBreakpoint

    const isPortraitSmall =
      isPortrait &&
      window.innerWidth <= portraitBreakpoint

    return {
      isPortrait,
      isLandscape,
      isStandalone,

      isPhone,
      isPhoneLandscape: isPhone && isLandscape,

      isMobileStandalone,
      isDesktopStandalone,

      isPortraitLarge,
      isPortraitSmall,
    }
  }

  const [state, setState] = useState(getState)

  useEffect(() => {
    let raf = 0

    const update = () => {
      cancelAnimationFrame(raf)

      raf = requestAnimationFrame(() => {
        setState(getState())
      })
    }

    update()

    window.addEventListener("resize", update)
    window.addEventListener("orientationchange", update)
    window.addEventListener("focus", update)

    document.addEventListener("visibilitychange", update)

    window.visualViewport?.addEventListener("resize", update)
    window.visualViewport?.addEventListener("scroll", update)

    return () => {
      cancelAnimationFrame(raf)

      window.removeEventListener("resize", update)
      window.removeEventListener("orientationchange", update)
      window.removeEventListener("focus", update)

      document.removeEventListener("visibilitychange", update)

      window.visualViewport?.removeEventListener("resize", update)
      window.visualViewport?.removeEventListener("scroll", update)
    }
  }, [portraitBreakpoint])

  const {
    isPortrait,
    isLandscape,
    isStandalone,

    isPhone,
    isPhoneLandscape,

    isMobileStandalone,
    isDesktopStandalone,

    isPortraitLarge,
    isPortraitSmall,
  } = state

  const mode: DeviceMode = isStandalone
    ? isPortrait
      ? "standalone-portrait"
      : "standalone-landscape"
    : isPortrait
      ? "portrait"
      : "landscape"

  return {
    isPortrait,
    isLandscape,
    isStandalone,

    mode,

    isMobileWeb: !isStandalone && isPortrait,

    isStandalonePortrait: isStandalone && isPortrait,
    isStandaloneLandscape: isStandalone && isLandscape,

    isPhone,
    isPhoneLandscape,

    isMobileStandalone,
    isDesktopStandalone,

    isPortraitLarge,
    isPortraitSmall,
  }
}